import { useState, useMemo } from 'react';
import { useSelector } from 'react-redux';
import {
    Box,
    Breadcrumb,
    BreadcrumbItem,
    BreadcrumbLink,
    Stack,
} from '@chakra-ui/react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { toast } from 'react-toastify';

import {
    Alert,
    AlertDescription,
    AlertIcon,
    Button,
    Card,
    Heading,
    LoadingIndicator,
} from '@/components/v4';
import { breadCrumbStyling } from 'components/forms/new/formStyles';
import { useQuery } from 'hooks/useCustomQuery';
import getEightByEightCatalogue from 'API/ServiceProfiles/getEightByEightCatalogue';
import getService from 'API/Service/getService';
import getEightByEightServiceLicenses from 'API/Service/getEightByEightServiceLicenses';
import updateEightByEightService from 'API/Service/updateEightByEightService';
import LicenceEntitlementsTree from 'pages/dashboards/v5/add/LicenceEntitlementsTree';
import {
    buildActiveMap,
    buildEightByEightServiceCatalogItems,
    initFromService,
} from './eightByEightLicenceMapping';

const PARTNER_SCOPE = 40;

export default function EightByEightLicences() {
    const { id: serviceId } = useParams();
    const { state: locationState } = useLocation();
    const navigate = useNavigate();
    const queryClient = useQueryClient();

    const { darkMode } = useSelector((state) => state.settings);
    const { currentCompany } = useSelector((state) => state.navigationLists);
    const accountScope = useSelector((state) => state.login.requiredScope);

    const companyID =
        typeof currentCompany === 'object'
            ? currentCompany?.id
            : currentCompany;
    const isEditMode =
        accountScope >= PARTNER_SCOPE && locationState?.mode !== 'view';

    const [isSaving, setIsSaving] = useState(false);
    const [saveError, setSaveError] = useState(null);
    const [entitlements, setEntitlements] = useState(null);

    const { data: catalogue = [], isLoading: catalogueLoading } = useQuery({
        queryKey: ['8x8CompanyAvailableLicences', companyID],
        queryFn: async () => {
            // Manage Licences shows the licences available to this company
            // (inherited from the associated partner service profile), not the
            // full platform catalogue.
            const res = await getEightByEightCatalogue({
                level: 'company',
                companyID,
            });
            return res.data || [];
        },
        enabled: !!serviceId && !!companyID,
        refetchOnWindowFocus: false,
    });

    const { data: serviceObj, isLoading: serviceLoading } = useQuery({
        queryKey: ['8x8Service', serviceId],
        queryFn: async () => {
            const res = await getService(serviceId);
            return res.data;
        },
        enabled: !!serviceId,
        refetchOnWindowFocus: false,
    });

    const { data: serviceLicences = [], isLoading: serviceLicencesLoading } =
        useQuery({
            queryKey: ['8x8ServiceLicences', serviceId],
            queryFn: () => getEightByEightServiceLicenses(serviceId),
            enabled: !!serviceId,
            refetchOnWindowFocus: false,
        });

    const resolvedEntitlements = useMemo(() => {
        if (entitlements !== null) return entitlements;
        if (catalogue.length) {
            return initFromService(catalogue, serviceLicences);
        }
        return [];
    }, [entitlements, catalogue, serviceLicences]);

    const activeMap = useMemo(
        () => buildActiveMap(serviceLicences),
        [serviceLicences],
    );

    const isLoading =
        catalogueLoading || serviceLoading || serviceLicencesLoading;

    const handleSave = async () => {
        setSaveError(null);
        setIsSaving(true);
        try {
            // PUT /Service/EightByEight replaces the entitlement set, so send
            // every item; disabled licences carry allowedQuantity 0.
            const catalogItems = buildEightByEightServiceCatalogItems({
                serviceId,
                entitlements: resolvedEntitlements,
                serviceCatalogItems: serviceObj?.catalogItems,
                catalogue,
            });

            await updateEightByEightService({
                id: serviceId,
                name: serviceObj?.name,
                catalogItems,
            });
            await queryClient.invalidateQueries(['8x8Service', serviceId]);
            await queryClient.invalidateQueries([
                '8x8ServiceLicences',
                serviceId,
            ]);
            toast.success('Licences updated. Order placed successfully.');
            navigate(-1);
        } catch (e) {
            setSaveError(
                e?.response?.data?.message ||
                    'Failed to update licences. Please try again.',
            );
        } finally {
            setIsSaving(false);
        }
    };

    if (isLoading) return <LoadingIndicator />;

    return (
        <Box maxWidth="1064px" position="relative">
            <Breadcrumb sx={breadCrumbStyling}>
                <BreadcrumbItem>
                    <BreadcrumbLink
                        color={darkMode ? 'gray.300' : undefined}
                        onClick={() => navigate('/dashboard-services')}>
                        Dashboard
                    </BreadcrumbLink>
                </BreadcrumbItem>
                <BreadcrumbItem isCurrentPage>
                    <BreadcrumbLink
                        color={darkMode ? 'whiteAlpha.600' : undefined}>
                        Manage Licences
                    </BreadcrumbLink>
                </BreadcrumbItem>
            </Breadcrumb>

            <Card
                borderRadius={15}
                p="30px"
                maxWidth="1000px"
                ml={0}
                mt={2}
                bg={darkMode ? 'dark.tcap' : null}>
                <Heading
                    as="h3"
                    size="md"
                    mb={4}
                    color={darkMode ? 'white' : 'gray.700'}>
                    Manage Licences
                </Heading>

                {saveError && (
                    <Alert status="error" variant="left-accent" mb={6}>
                        <AlertIcon />
                        <AlertDescription>{saveError}</AlertDescription>
                    </Alert>
                )}

                <LicenceEntitlementsTree
                    catalogue={catalogue}
                    value={resolvedEntitlements}
                    onChange={isEditMode ? setEntitlements : undefined}
                    darkMode={darkMode}
                    hasActive
                    activeMap={activeMap}
                    isReadOnly={!isEditMode}
                />
            </Card>

            <Stack direction="row" spacing={3} mt={5}>
                <Button variant="outline" onClick={() => navigate(-1)}>
                    Back
                </Button>
                {isEditMode && (
                    <Button
                        isLoading={isSaving}
                        loadingText="Saving"
                        onClick={handleSave}>
                        Save
                    </Button>
                )}
            </Stack>
        </Box>
    );
}
